import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeriesHeader from '../components/brand/SeriesHeader';
import SeriesFooter from '../components/brand/SeriesFooter';
import { 
  ReviewHero, 
  HowItWorks, 
  PainPoints, 
  FinalCTA 
} from '../components/review-hero';
import GuidedInput from '../components/review-hero/GuidedInput';
import GatedReviewDisplay from '../components/review-hero/GatedReviewDisplay';
import { getSeriesBySlug } from '../data/series';
import { Loader2 } from 'lucide-react';

/**
 * HP.2 + RV.1-3: "Performance Review Coming?" Hero Page with Acquisition Funnel
 * 
 * User Flow:
 * 1. Land on page, see hero with "Start Your Review" CTA
 * 2. Click CTA → Open Guided Input wizard (RV.1)
 * 3. Complete 3 steps → Generate review (RV.2)
 * 4. See gated review output (RV.3)
 * 5. Click "Unlock" → Signup → Complete page (RV.4)
 */

const ReviewHeroPage = () => {
  const navigate = useNavigate();
  
  // Flow states
  const [flowState, setFlowState] = useState('hero'); // 'hero' | 'input' | 'generating' | 'gated'
  const [inputData, setInputData] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const series = getSeriesBySlug('it-leadership') || {
    name: '30 Day Skills',
    accentColor: '#dc2626',
    slug: 'it-leadership'
  };

  // Check if user came back from signup with a pending review
  useEffect(() => {
    const pendingUnlock = sessionStorage.getItem('rv_pending_unlock');
    if (pendingUnlock) {
      // User has a pending review - redirect to completion
      const { review_id, input_hash } = JSON.parse(pendingUnlock);
      navigate(`/review/complete?rv_id=${review_id}&hash=${input_hash}`);
    }
  }, []);

  const handleStartInput = () => {
    setFlowState('input');
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputComplete = async (data) => {
    setInputData(data);
    setFlowState('generating');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/review/generate-guest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to generate review');
      }

      const result = await response.json();
      setReviewData(result);
      setFlowState('gated');

    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message);
      setFlowState('input');
    }
  };

  const handleInputCancel = () => {
    setFlowState('hero');
    setInputData(null);
  };

  const handleUnlock = (returnUrl) => {
    navigate(`/login?return=${encodeURIComponent(returnUrl)}`);
  };

  const handleStartFresh = () => {
    setFlowState('input');
    setReviewData(null);
    setInputData(null);
    sessionStorage.removeItem('rv_input');
    sessionStorage.removeItem('rv_pending_unlock');
  };

  // Render based on flow state
  const renderMainContent = () => {
    switch (flowState) {
      case 'input':
        return (
          <section className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center py-20 px-6">
            <GuidedInput
              onComplete={handleInputComplete}
              onCancel={handleInputCancel}
              accentColor={series.accentColor}
            />
          </section>
        );

      case 'generating':
        return (
          <section className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
            <div className="text-center text-white" data-testid="rv-generating">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-red-400" />
              <h2 className="text-xl font-semibold mb-2">Writing your review...</h2>
              <div className="space-y-2 text-slate-400 text-sm">
                <p className="animate-pulse">Analyzing your achievements...</p>
                <p className="animate-pulse delay-75">Identifying demonstrated skills...</p>
                <p className="animate-pulse delay-150">Crafting impact statements...</p>
              </div>
            </div>
          </section>
        );

      case 'gated':
        return (
          <section className="min-h-screen bg-slate-100 py-20 px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Your review is ready!
              </h2>
              <p className="text-slate-600">
                Here's what we generated from your input
              </p>
            </div>
            <GatedReviewDisplay
              reviewData={reviewData}
              onUnlock={handleUnlock}
              onStartFresh={handleStartFresh}
              accentColor={series.accentColor}
            />
          </section>
        );

      default: // 'hero'
        return (
          <>
            <ReviewHeroWithCTA 
              series={series} 
              onStartReview={handleStartInput}
            />
            <PainPoints series={series} />
            <HowItWorks series={series} />
            <ReviewDemoSection series={series} />
            <FinalCTA series={series} />
          </>
        );
    }
  };

  return (
    <div className="review-hero-page min-h-screen bg-slate-50" data-testid="review-hero-page">
      <SeriesHeader series={series} />
      
      <main>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-center">
            {error}
            <button 
              onClick={() => setError(null)}
              className="ml-4 underline"
            >
              Dismiss
            </button>
          </div>
        )}
        {renderMainContent()}
      </main>
      
      {flowState === 'hero' && <SeriesFooter series={series} />}
    </div>
  );
};

/**
 * Modified ReviewHero with "Start Your Review" CTA that triggers the input flow
 */
const ReviewHeroWithCTA = ({ series, onStartReview }) => {
  const accentColor = series?.accentColor || '#dc2626';

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden pt-20" data-testid="review-hero-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div 
          className="absolute top-20 right-20 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: accentColor }}
        />
        <div 
          className="absolute bottom-40 left-20 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: '#3b82f6' }}
        />
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="h-full w-full" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-8"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Takes 3 minutes, not 3 hours
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            <span className="text-white">Performance review </span>
            <span style={{ color: accentColor }}>coming?</span>
          </h1>

          {/* Subhead */}
          <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            Stop scrambling to remember what you did. 
            <span className="text-white font-medium"> We'll help you write a compelling self-review</span> in minutes, not hours.
          </p>

          {/* CTA */}
          <button
            onClick={onStartReview}
            className="inline-flex items-center gap-3 px-8 py-4 text-lg font-medium text-white rounded-xl transition-all hover:scale-105 shadow-lg"
            style={{ backgroundColor: accentColor }}
            data-testid="rv-start-review-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Start Your Review
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-8 mt-10 text-slate-400">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">No signup to start</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span className="text-sm">AI-powered</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-sm">Private & secure</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Demo section showing example output
 */
const ReviewDemoSection = ({ series }) => {
  const accentColor = series?.accentColor || '#dc2626';

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6"
              style={{ backgroundColor: `${accentColor}10`, color: accentColor }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              See what you'll get
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Your review, structured and ready
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Here's an example of what your generated self-review looks like. Professional, evidence-based, and easy to customize.
            </p>
          </div>

          {/* Example Output Card */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${accentColor}15` }}
                >
                  <svg className="w-5 h-5" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Q1 2026 Self-Review</p>
                  <p className="text-sm text-slate-500">Example output</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">Summary</h4>
                <p className="text-slate-800 leading-relaxed">
                  This quarter, I drove significant improvements in team efficiency and project delivery while developing my technical leadership capabilities. My focus on process optimization and team mentoring has resulted in measurable improvements in velocity and quality.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-medium">Key Achievements</h4>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="font-medium text-slate-900 mb-1">Led CRM Migration Project</p>
                  <p className="text-sm text-slate-600 mb-2">
                    Successfully delivered the CRM migration ahead of schedule, resulting in 40% reduction in customer lookup time and improved team satisfaction scores.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600">Project Management</span>
                    <span className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600">Stakeholder Communication</span>
                    <span className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600">Technical Leadership</span>
                  </div>
                </div>
              </div>

              <p className="text-center text-sm text-slate-500">
                + more achievements, development areas, and goals in the full review
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewHeroPage;
